import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./dashboard.css";
import { useSelector } from "react-redux";
import { getCompany, getUserName } from "../../../Redux/selecters";
import ApiService from "../../services/apiService";
import moment from "moment";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell, LabelList,
} from "recharts";
import {
    FaShoppingCart,
    FaTruck,
    FaClipboardList,
    FaUsers,
    FaFileInvoiceDollar,
    FaCalendarAlt,
    FaArrowUp,
    FaArrowDown,
    FaBoxes, FaEye,
    FaChevronDown,
} from "react-icons/fa";
import { FaSackDollar } from "react-icons/fa6";
import { IoDocumentTextOutline } from "react-icons/io5";

const STATUS_COLORS = {
    Approved: "#13A878",
    Pending: "#F5B82E",
    Rejected: "#EF5350",
    Cancelled: "#98A2B3",
    Hold: "#F97316",
    Closed: "#4C8DFF",
};

const FILTER_OPTIONS = [
    { value: "today", label: "Today", helper: "Today" },
    { value: "yesterday", label: "Yesterday", helper: "Yesterday" },
    { value: "last3days", label: "Last 3 Days", helper: "Last 3 Days" },
    { value: "last7days", label: "Last 7 Days", helper: "Last 7 Days" },
    { value: "last30days", label: "Last 30 Days", helper: "Last 30 Days" },
    { value: "last3months", label: "Last 3 Months", helper: "Last 3 Months" },
    { value: "last6months", label: "Last 6 Months", helper: "Last 6 Months" },
    { value: "lastyear", label: "Last Year", helper: "Last Year" },
];

const emptyDashboard = {
    dashboardCount: {
        totalPO: 0,
        totalPOPendingForApproval: 0,
        totalRequisition: 0,
        totalRequisitionPendingForApproval: 0,
        totalPOAmount: 0,
        totalGRN: 0,
    },
    monthlyPOAmount: [],
    poStatus: [],
    prStatus: [],
    recentPO: [],
    recentRequisition: [],
    pendingPOVendor: [],
    prStatusSummary: [],
    topRequestedItems: [],
    latestRequisition: [],
    latestPO: [],
    latestGRN: [],
    poPropertyWise: [],
};

const formatCurrency = (value) => `${Number(value || 0)?.toFixed(3)}`;

const formatNumber = (value) => Number(value || 0).toLocaleString("en-BH");

const formatCompactCurrency = (value) => {
    const n = Number(value || 0);
    if (Math.abs(n) >= 1000000) return ` ${(n / 1000000).toFixed(3)}M`;
    if (Math.abs(n) >= 1000) return ` ${(n / 1000).toFixed(3)}K`;
    return ` ${Math.round(n)}`;
};

const getStatusColor = (name) => STATUS_COLORS[name] || "#98A2B3";

const statusPillClass = (statusDesc) => {
    const key = (statusDesc || "").toLowerCase();
    if (key.includes("approved")) return "pill pill-approved";
    if (key.includes("pending")) return "pill pill-pending";
    if (key.includes("reject") || key.includes("cancel")) return "pill pill-rejected";
    if (key.includes("hold")) return "pill pill-hold";
    if (key.includes("close")) return "pill pill-closed";
    return "pill";
};

const SectionHeader = ({ title, subtitle, action }) => (
    <div className="section-head">
        <div>
            <div className="section-title">{title}</div>
            {subtitle && <div className="section-subtitle">{subtitle}</div>}
        </div>
        {action}
    </div>
);
const KpiCard = ({
    icon,
    colorClass,
    label,
    value,
    caption,
    emphasis,
    onEyeClick,
}) => (
    <div className={`kpi-card ${emphasis ? "kpi-emphasis" : ""}`}>
        <div className={`kpi-icon ${colorClass}`}>
            {icon}
        </div>

        <div className="kpi-main">
            <div className="kpi-label">{label}</div>
            <div className="kpi-value">{value}</div>
            <div className="kpi-caption">{caption}</div>
        </div>

        {onEyeClick && (
            <button
                type="button"
                className="kpi-eye-btn"
                onClick={onEyeClick}
                title={`View ${label}`}
            >
                <FaEye />
            </button>
        )}

        <div className={`kpi-accent ${colorClass}`} />
    </div>
);
const StatusDonut = ({
    title,
    data,
    totalLabel,
    emptyText,
    onStatusClick,
}) => {
    const chartData = (data || [])
        .map((d) => ({
            name: d.statusName,
            value: Number(d.totalCount || 0),
        })).filter((d) => d.value > 0).map((d) => ({ ...d, color: getStatusColor(d.name) }));

    const total = chartData.reduce((sum, d) => sum + d.value, 0);
    debugger
    return (
        <div className="panel chart-panel">
            <SectionHeader title={title} subtitle="Status distribution" />
            {chartData.length === 0 ? (
                <div className="empty-state compact-empty">
                    <div className="empty-icon"><FaBoxes /></div>
                    <div>{emptyText || "No status data available"}</div>
                </div>
            ) : (
                <div className="donut-layout">
                    <div className="donut-area">
                        <ResponsiveContainer width="100%" height={190}>
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    innerRadius={58}
                                    outerRadius={82}
                                    paddingAngle={3}
                                    stroke="none"
                                    startAngle={90}
                                    endAngle={-270}
                                    onClick={(data) => {
                                        if (onStatusClick && data?.name) {
                                            onStatusClick(data.name);
                                        }
                                    }}
                                    style={{ cursor: "pointer" }}
                                >
                                    {chartData.map((entry, idx) => (
                                        <Cell key={idx} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(value) => formatNumber(value)}
                                    contentStyle={{ borderRadius: 10, border: "1px solid #E5EAF2", fontSize: 12 }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="donut-center">
                            <strong>{formatNumber(total)}</strong>
                            <span>{totalLabel}</span>
                        </div>
                    </div>
                    <div className="status-list">
                        {chartData?.map((entry) => {
                            const percent = total ? Math.round((entry.value / total) * 100) : 0;
                            return (
                                <div
                                    className="status-item"
                                    key={entry.name}
                                    onClick={() => onStatusClick?.(entry.name)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="status-item-top">
                                        <span className="status-name">
                                            <i style={{ background: entry.color }} />
                                            {entry.name}
                                        </span>
                                        <strong>{formatNumber(entry.value)}</strong>
                                    </div>
                                    <div className="status-track">
                                        <span style={{ width: `${percent}%`, background: entry.color }} />
                                    </div>
                                    <span className="status-percent">{percent}%</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const PurchaseOrderTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const row = payload[0]?.payload || {};
    return (
        <div style={{
            background: "#fff",
            border: "1px solid #E6EAF1",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(31,48,76,.10)",
            padding: "10px 12px",
            fontSize: 12,
        }}>
            <div style={{ color: "#172B4D", fontWeight: 700, marginBottom: 6 }}>{label}</div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 18 }}>
                <span>PO Amount</span>
                <strong>{formatCurrency(row.amount)}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 18, marginTop: 4 }}>
                <span>PO Count</span>
                <strong>{formatNumber(row.totalCount)}</strong>
            </div>
        </div>
    );
};

const DataTable = ({ title, subtitle, columns, rows, }) => (
    // const DataTable = ({ title, subtitle, columns, rows, actionLabel = "View all" }) => (
    <div className="panel table-panel">
        <SectionHeader
            title={title}
            subtitle={subtitle}
        // action={actionLabel ? <button type="button" className="view-all-btn">{actionLabel} <span>→</span></button> : null}
        />
        <div className="table-scroll">
            <table className="db-table">
                <thead>
                    <tr>
                        {columns.map((col) => <th key={col.key}>{col.header}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length}>
                                <div className="empty-table">No data available for this period</div>
                            </td>
                        </tr>
                    ) : rows.map((row, idx) => (
                        <tr key={`${row?.pOrderNo || row?.requisitionNo || idx}-${idx}`}>
                            {columns.map((col) => (
                                <td key={col.key}>{col.render ? col.render(row) : (row[col.key] ?? "—")}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

const DashBoard = () => {
    const apiService = new ApiService();
    const navigate = useNavigate();
    const Name = useSelector(getUserName);
    const companies = useSelector(getCompany);
    const [dashboardData, setDashboardData] = useState(emptyDashboard);
    const [selectedPeriod, setSelectedPeriod] = useState("lastyear");
    const [permission, setPermission] = useState();
    const [loading, setLoading] = useState(false);

    const fetchDashboard = async (period = selectedPeriod) => {
        try {
            setLoading(true);

            // Backend should read: period = year | month | week | today.
            // If your API uses another parameter name, change only `period` below.
            const param = `?Filter=${encodeURIComponent(period || 'lastyear')}`;
            const res = await apiService.getApiParamater("GET_PROCUREMENT_DASHBOARD", param);

            if (res?.data?.isSuccessful && res?.data?.result) {
                setDashboardData({ ...emptyDashboard, ...res.data.result });
            } else {
                setDashboardData(emptyDashboard);
            }
        } catch (error) {
            console.error("Dashboard fetch error:", error);
            setDashboardData(emptyDashboard);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const storedPermissions = JSON.parse(localStorage.getItem("RoleAccess")) || [];
        const dashboardPermission = storedPermissions?.find((acc) => acc?.formName === "Dashboard");
        setPermission(dashboardPermission?.canView)
        if (dashboardPermission?.canView) fetchDashboard("lastyear");

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Name, companies?.selectedCompany?.companyNo]);

    const handlePeriodChange = (event) => {
        const value = event.target.value;
        setSelectedPeriod(value);
        fetchDashboard(value);
    };
    const { dashboardCount } = dashboardData;

    const prDonutData = useMemo(() => {
        if (dashboardData.prStatus?.length) return dashboardData.prStatus;
        return dashboardData.prStatusSummary.map((item) => ({
            statusDesc: item.statusDesc,
            totalCount: item.totalCount,
        }));
    }, [dashboardData.prStatus, dashboardData.prStatusSummary]);

    const purchaseTrend = useMemo(() => {
        return (dashboardData.monthlyPOAmount || []).map((item) => ({
            ...item,
            amount: Number(item.totalPOAmount || 0),
            shortLabel: item.monthLabel || moment(item.monthStartDate).format("MMM YYYY"),
        }));
    }, [dashboardData.monthlyPOAmount]);

    const trendSummary = useMemo(() => {
        if (!purchaseTrend.length) return { total: 0, peak: 0, peakLabel: "—" };
        const total = purchaseTrend.reduce((sum, item) => sum + item.amount, 0);
        const peakItem = purchaseTrend.reduce((max, item) => item.amount > max.amount ? item : max, purchaseTrend[0]);
        return { total, peak: peakItem.amount, peakLabel: peakItem.shortLabel };
    }, [purchaseTrend]);

    const propertyPOTrend = useMemo(() => {
        return (dashboardData.poPropertyWise || []).map((item) => ({
            ...item,
            amount: Number(item.totalPOAmount || 0),
            count: Number(item.totalCount || 0),
            propertyLabel: item.propertyName || "—",
        }));
    }, [dashboardData.poPropertyWise]);

    const activity = useMemo(() => [
        ...dashboardData.latestPO.map((p) => ({
            type: "po",
            text: `Purchase Order ${p.pOrderNo} updated`,
            sub: p.vendorName || "Purchase order",
            date: p.modifiedOn,
        })),
        ...dashboardData.latestRequisition.map((r) => ({
            type: "req",
            text: `Requisition ${r.requisitionNo} updated`,
            sub: r.requestor || "Requisition",
            date: r.modifiedOn,
        })),
        ...dashboardData.latestGRN.map((g) => ({
            type: "grn",
            text: `Goods Received ${g.grnNo} updated`,
            sub: g.vendorName || "Goods receipt",
            date: g.modifiedOn,
        })),
    ])
        .filter((a) => a.date)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 6);

    const selectedFilter = FILTER_OPTIONS.find((x) => x.value === selectedPeriod) || FILTER_OPTIONS[0];
    const getDashboardDateRange = (period) => {
        const today = moment();

        let fromDate;
        let toDate;

        switch (period) {
            case "today":
                fromDate = today.clone().startOf("day");
                toDate = today.clone().endOf("day");
                break;

            case "yesterday":
                fromDate = today.clone().subtract(1, "day").startOf("day");
                toDate = today.clone().subtract(1, "day").endOf("day");
                break;

            case "last3days":
                fromDate = today.clone().subtract(2, "days").startOf("day");
                toDate = today.clone().endOf("day");
                break;

            case "last7days":
                fromDate = today.clone().subtract(6, "days").startOf("day");
                toDate = today.clone().endOf("day");
                break;

            case "last30days":
                fromDate = today.clone().subtract(29, "days").startOf("day");
                toDate = today.clone().endOf("day");
                break;

            case "last3months":
                fromDate = today.clone().subtract(3, "months").startOf("day");
                toDate = today.clone().endOf("day");
                break;

            case "last6months":
                fromDate = today.clone().subtract(6, "months").startOf("day");
                toDate = today.clone().endOf("day");
                break;

            case "lastyear":
            default:
                fromDate = today.clone().subtract(1, "year").startOf("day");
                toDate = today.clone().endOf("day");
                break;
        }

        return {
            fromDate: fromDate.format("YYYY-MM-DD"),
            toDate: toDate.format("YYYY-MM-DD"),
        };
    };
    const getFilterState = (extra = {}) => {
        const { fromDate, toDate } = getDashboardDateRange(selectedPeriod);

        return {
            fromDate,
            toDate,
            ...extra,
        };
    };
    const handleDashboardNavigation = (path, extra = {}) => {
        navigate(path, {
            state: getFilterState(extra),
        });
    };
    return (
        <>
            {permission === true ?
                <div className="db-container">
                    <div className="dashboard-hero d-flex justify-content-between">
                        <div className="hero-copy">
                            <div className="eyebrow"><span /> PROCUREMENT &amp; INVENTORY</div>
                            <h1>Procurement Dashboard</h1>
                            {/* <p>One place to monitor purchasing, requisitions, approvals and supplier activity.</p> */}
                            <div className="hero-meta">
                                <span><i /> Live dashboard</span>
                                <span>•</span>
                                <span>{selectedFilter.helper}</span>
                            </div>
                        </div>
                        <div className="hero-filter">
                            <div className="filter-caption"><FaCalendarAlt /> Reporting period</div>
                            <div className="select-shell">
                                <select value={selectedPeriod} onChange={handlePeriodChange} aria-label="Dashboard reporting period">
                                    {FILTER_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                <FaChevronDown className="select-arrow" />
                            </div>
                            {/* <div className="filter-date">As of {moment().format("DD MMM YYYY")}</div> */}
                        </div>
                    </div>

                    {loading && (
                        <div className="loading-strip">
                            <span className="loading-dot" /> Refreshing dashboard for <strong>{selectedFilter.label}</strong>...
                        </div>
                    )}

                    <div className="kpi-grid">

                        {/* Purchase Orders */}
                        <KpiCard
                            icon={<FaShoppingCart />}
                            colorClass="kpi-blue"
                            label="Purchase Orders"
                            value={formatNumber(dashboardCount.totalPO)}
                            caption="Total orders"
                            onEyeClick={() =>
                                handleDashboardNavigation(
                                    "/Inventory/Reports/PurchaseOrder",
                                    {
                                        documentType: "PO",
                                    }
                                )
                            }
                        />

                        {/* Pending PO Approvals */}
                        <KpiCard
                            icon={<IoDocumentTextOutline />}
                            colorClass="kpi-green"
                            label="Pending PO Approvals"
                            value={formatNumber(dashboardCount.totalPOPendingForApproval)}
                            caption="Awaiting approval"
                            onEyeClick={() =>
                                handleDashboardNavigation(
                                    "/Inventory/Reports/PurchaseOrder",
                                    {
                                        status: "Pending",
                                        documentType: "PO",
                                    }
                                )
                            }
                        />

                        {/* Purchase Requisitions */}
                        <KpiCard
                            icon={<FaClipboardList />}
                            colorClass="kpi-purple"
                            label="Purchase Requisitions"
                            value={formatNumber(dashboardCount.totalRequisition)}
                            caption="Total requisitions"
                            onEyeClick={() =>
                                handleDashboardNavigation(
                                    "/Inventory/Reports/PurchaseRequisition",
                                    {
                                        documentType: "Requisition",
                                    }
                                )
                            }
                        />

                        {/* Pending Requisitions */}
                        <KpiCard
                            icon={<FaUsers />}
                            colorClass="kpi-orange"
                            label="Pending Requisitions"
                            value={formatNumber(dashboardCount.totalRequisitionPendingForApproval)}
                            caption="Need attention"
                            onEyeClick={() =>
                                handleDashboardNavigation(
                                    "/Inventory/Reports/PurchaseRequisition",
                                    {
                                        status: "Pending",
                                        documentType: "Requisition",
                                    }
                                )
                            }
                        />

                        {/* Purchase Value - no eye button as requested */}
                        <KpiCard
                            icon={<FaSackDollar />}
                            colorClass="kpi-teal"
                            label="Purchase Value"
                            value={dashboardCount.totalPOAmount?.toFixed(3)}
                            caption="Total PO value"
                            emphasis
                        />

                        {/* Total GRN */}
                        <KpiCard
                            icon={<FaTruck />}
                            colorClass="kpi-indigo"
                            label="Total GRN"
                            value={formatNumber(dashboardCount.totalGRN)}
                            caption="Total GRN count"
                            onEyeClick={() =>
                                handleDashboardNavigation(
                                    "/Inventory/Reports/grn",
                                    {
                                        documentType: "GRN",
                                    }
                                )
                            }
                        />

                    </div>

                    <div className="dashboard-grid top-grid">
                        <div className="panel trend-panel">
                            <SectionHeader
                                title="Purchase Order Analysis"
                                subtitle="PO amount by month"
                                action={
                                    <div className="trend-summary">
                                        <span>Total {formatCompactCurrency(trendSummary.total)}</span>
                                        <b>Peak {formatCompactCurrency(trendSummary.peak)}</b>
                                    </div>
                                }
                            />
                            {purchaseTrend.length === 0 ? (
                                <div className="empty-state"><div className="empty-icon"><FaSackDollar /></div>No purchase trend data available</div>
                            ) : (
                                <div className="chart-area">
                                    <ResponsiveContainer width="100%" height={250}>
                                        <BarChart
                                            data={purchaseTrend}
                                            margin={{ top: 25, right: 8, left: 0, bottom: 0 }}
                                            barCategoryGap="34%"
                                        >
                                            <defs>
                                                <linearGradient
                                                    id="purchaseBarGradient"
                                                    x1="0"
                                                    y1="0"
                                                    x2="0"
                                                    y2="1"
                                                >
                                                    <stop offset="0%" stopColor="#7357D8" />
                                                    <stop offset="100%" stopColor="#4D83E8" />
                                                </linearGradient>
                                            </defs>

                                            <CartesianGrid
                                                stroke="#EDF1F6"
                                                vertical={false}
                                            />

                                            <XAxis
                                                dataKey="shortLabel"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{
                                                    fill: "#7B8798",
                                                    fontSize: 10
                                                }}
                                            />

                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{
                                                    fill: "#8995A6",
                                                    fontSize: 10
                                                }}
                                                tickFormatter={formatCompactCurrency}
                                                width={48}
                                            />

                                            <Tooltip
                                                cursor={{ fill: "#F7F8FC" }}
                                                content={<PurchaseOrderTooltip />}
                                            />

                                            <Bar
                                                dataKey="amount"
                                                fill="url(#purchaseBarGradient)"
                                                radius={[6, 6, 0, 0]}
                                                maxBarSize={42}
                                            >
                                                <LabelList
                                                    dataKey="amount"
                                                    position="top"
                                                    // formatter={formatCompactCurrency}
                                                    fill="#172B4D"
                                                    fontSize={10}
                                                    fontWeight={600}
                                                />
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {purchaseTrend.length > 0 && (
                                <div className="trend-footer">
                                    <span><i className="legend-square" /> Purchase amount</span>
                                    <span>Highest: <strong>{trendSummary.peakLabel}</strong></span>
                                </div>
                            )}
                        </div>
                        <StatusDonut
                            title="Purchase Order Status"
                            data={dashboardData.poStatus}
                            totalLabel="Total POs"
                            emptyText="PO status data is not available"
                            onStatusClick={(status) =>
                                handleDashboardNavigation(
                                    "/Inventory/Reports/PurchaseOrder",
                                    {
                                        status: status,
                                        documentType: "PO",
                                    }
                                )
                            }
                        />
                        <StatusDonut
                            title="Requisition Status"
                            data={prDonutData}
                            totalLabel="Total Reqs"
                            emptyText="Requisition status data is not available"
                            onStatusClick={(status) =>
                                handleDashboardNavigation(
                                    "/Inventory/Reports/PurchaseRequisition",
                                    {
                                        status: status,
                                        documentType: "Requisition",
                                    }
                                )
                            }
                        />
                    </div>

                    <div className="panel trend-panel" style={{ gridColumn: "1 / -1" }}>
                        <SectionHeader
                            title="Purchase Orders by Property"
                            subtitle="PO amount by property"
                            action={
                                <div className="trend-summary">
                                    <span>Total {formatCompactCurrency(propertyPOTrend.reduce((sum, item) => sum + item.amount, 0))}</span>
                                    <b>{formatNumber(propertyPOTrend.reduce((sum, item) => sum + item.count, 0))} POs</b>
                                </div>
                            }
                        />
                        {propertyPOTrend.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon"><FaSackDollar /></div>
                                No property-wise purchase order data available
                            </div>
                        ) : (
                            <div className="chart-area">
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart
                                        data={propertyPOTrend}
                                        margin={{ top: 25, right: 8, left: 0, bottom: 0 }}
                                        barCategoryGap="34%"
                                    >
                                        <defs>
                                            <linearGradient
                                                id="propertyPOBarGradient"
                                                x1="0"
                                                y1="0"
                                                x2="0"
                                                y2="1"
                                            >
                                                <stop offset="0%" stopColor="#7357D8" />
                                                <stop offset="100%" stopColor="#4D83E8" />
                                            </linearGradient>
                                        </defs>

                                        <CartesianGrid
                                            stroke="#EDF1F6"
                                            vertical={false}
                                        />

                                        <XAxis
                                            dataKey="propertyLabel"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{
                                                fill: "#7B8798",
                                                fontSize: 10
                                            }}
                                        />

                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{
                                                fill: "#8995A6",
                                                fontSize: 10
                                            }}
                                            tickFormatter={formatCompactCurrency}
                                            width={48}
                                        />

                                        <Tooltip
                                            cursor={{ fill: "#F7F8FC" }}
                                            content={({ active, payload, label }) => {
                                                if (!active || !payload?.length) return null;
                                                const row = payload[0]?.payload || {};
                                                return (
                                                    <div style={{
                                                        background: "#fff",
                                                        border: "1px solid #E6EAF1",
                                                        borderRadius: 12,
                                                        boxShadow: "0 8px 24px rgba(31,48,76,.10)",
                                                        padding: "10px 12px",
                                                        fontSize: 12,
                                                    }}>
                                                        <div style={{ color: "#172B4D", fontWeight: 700, marginBottom: 6 }}>{label}</div>
                                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 18 }}>
                                                            <span>PO Amount</span>
                                                            <strong>{formatCurrency(row.amount)}</strong>
                                                        </div>
                                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 18, marginTop: 4 }}>
                                                            <span>PO Count</span>
                                                            <strong>{formatNumber(row.count)}</strong>
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        />

                                        <Bar
                                            dataKey="amount"
                                            fill="url(#propertyPOBarGradient)"
                                            radius={[6, 6, 0, 0]}
                                            maxBarSize={42}
                                        >
                                            <LabelList
                                                dataKey="amount"
                                                // dataKey1="count"
                                                position="top"
                                                fill="#172B4D"
                                                fontSize={10}
                                                fontWeight={600}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                        {propertyPOTrend.length > 0 && (
                            <div className="trend-footer">
                                <span><i className="legend-square" /> Purchase amount</span>
                                <span>Hover a bar to see <strong>PO count</strong></span>
                            </div>
                        )}
                    </div>

                    <div className="dashboard-grid table-grid mt-3 ">
                        <DataTable
                            title="Recent Purchase Orders"
                            subtitle="Latest PO activity"
                            columns={[
                                { key: "pOrderNo", header: "PO No.", render: (r) => <span className="doc-number">{r.pOrderNo}</span> },
                                { key: "vendorName", header: "Supplier" },
                                { key: "amount", header: "Amount", render: (r) => <strong className="amount">{formatCurrency(r.amount?.toFixed(3))}</strong> },
                                { key: "statusDesc", header: "Status", render: (r) => <span className={statusPillClass(r.statusDesc)}>{r.statusDesc}</span> },
                            ]}
                            rows={dashboardData.recentPO}
                        />
                        <DataTable
                            title="Recent Requisitions"
                            subtitle="Latest request activity"
                            columns={[
                                { key: "requisitionNo", header: "Req. No.", render: (r) => <span className="doc-number">{r.requisitionNo}</span> },
                                { key: "requestor", header: "Requested By" },
                                // { key: "amount", header: "Amount", render: (r) => <strong className="amount">{formatCurrency(r.amount?.toFixed(3))}</strong> },
                                { key: "statusDesc", header: "Status", render: (r) => <span className={statusPillClass(r.statusDesc)}>{r.statusDesc}</span> },
                            ]}
                            rows={dashboardData.recentRequisition}
                        />
                    </div>

                    <div className="dashboard-grid bottom-grid">
                        <div className="panel summary-panel">
                            <SectionHeader title="Requisition Overview" subtitle="Approved vs pending" />
                            {dashboardData.prStatusSummary.length === 0 ? (
                                <div className="empty-state compact-empty"><div className="empty-icon"><FaClipboardList /></div>No requisition summary available</div>
                            ) : (
                                <div className="summary-list">
                                    {dashboardData.prStatusSummary.map((item) => {
                                        const total = dashboardData.prStatusSummary.reduce((s, x) => s + Number(x.totalCount || 0), 0);
                                        const count = Number(item.totalCount || 0);
                                        const pct = total ? Math.round((count / total) * 100) : 0;
                                        const color = getStatusColor(item.statusDesc);
                                        return (
                                            <div className="summary-row" key={item.statusDesc}>
                                                <div className="summary-label"><i style={{ background: color }} />{item.statusDesc}</div>
                                                <div className="summary-progress"><span style={{ width: `${pct}%`, background: color }} /></div>
                                                <strong>{formatNumber(count)}</strong>
                                                <em>{pct}%</em>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <DataTable
                            title="Top Requested Items"
                            subtitle="Most requested inventory"
                            columns={[
                                { key: "itemCode", header: "Code" },
                                { key: "itemDescription", header: "Item" },
                                { key: "quantity", header: "Qty", render: (r) => <strong>{formatNumber(r.quantity)}</strong> },
                            ]}
                            rows={dashboardData.topRequestedItems}
                        // actionLabel="View all"
                        />

                        <div className="panel activity-panel">
                            <SectionHeader title="Recent Activity" subtitle="Latest system events" />
                            {activity.length === 0 ? (
                                <div className="empty-state compact-empty"><div className="empty-icon"><FaFileInvoiceDollar /></div>No recent activity available</div>
                            ) : (
                                <div className="activity-list">
                                    {activity.map((item, idx) => (
                                        <div className="activity-row" key={`${item.text}-${idx}`}>
                                            <div className={`activity-icon activity-${item.type}`}>
                                                {item.type === "po" ? <IoDocumentTextOutline /> : item.type === "req" ? <FaClipboardList /> : <FaTruck />}
                                            </div>
                                            <div className="activity-copy">
                                                <strong>{item.text}</strong>
                                                <span>{item.sub}</span>
                                            </div>
                                            <time>{moment(item.date).fromNow()}</time>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                : ""}
        </>
    );
};

export default DashBoard;